import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";

/**
 * Lists every per-seller-profile research run that exists for the current
 * contact. Clicking an entry switches the active seller — the parent's
 * useEffect picks that up and re-fetches the right overview record.
 *
 * Stays silent if there's only one (or zero) historical run, so we don't add
 * UI noise for the common case.
 */
const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const day = 24 * 60 * 60 * 1000;
    if (diffMs < day) return "today";
    if (diffMs < 2 * day) return "yesterday";
    if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const qualityColor = (q) => {
  const k = (q && q.quality) || "";
  if (k === "rich") return "#059669";
  if (k === "moderate") return "#d97706";
  if (k === "sparse") return "#dc2626";
  return "#9ca3af";
};

const ResearchHistoryStrip = observer(({ contactId, activeSellerProfileId, onSelectSeller, refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Refetches when:
  //   - contactId changes (switching contacts)
  //   - refreshKey changes (parent bumps it after a re-run completes → a new
  //     seller-research entry may have been added that we need to surface)
  //   - activeSellerProfileId changes (defensive — if the rep just picked a
  //     new seller via the dropdown, the strip's "active" pill needs to flip;
  //     also catches the case where a fresh run finished and the parent
  //     hasn't bumped refreshKey yet)
  useEffect(() => {
    if (!contactId) {
      setEntries([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    makeApi({
      url: `${URLS.deepResearchOverviewHistory}/${contactId}/history`,
      method: "GET"
    }).then(res => {
      if (cancelled) return;
      const list = Array.isArray(res?.data?.entries) ? res.data.entries : [];
      setEntries(list);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setEntries([]);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [contactId, refreshKey, activeSellerProfileId]);

  // Don't render for legacy single-seller contacts — would just be noise.
  if (loading || entries.length < 2) return null;

  return (
    <div className="drHistoryStrip">
      <div className="drHistoryHeader">
        <span className="drHistoryLabel">Research history</span>
        <span className="drHistoryHint">{entries.length} runs · click to view</span>
      </div>
      <div className="drHistoryList">
        {entries.map((e) => {
          const isActive = e.sellerProfileId === activeSellerProfileId;
          return (
            <button
              key={e.sellerProfileId}
              type="button"
              className={`drHistoryItem ${isActive ? "drHistoryItem--active" : ""}`}
              onClick={() => {
                if (!isActive && onSelectSeller) onSelectSeller(e.sellerProfileId);
              }}
              title={`Seller: ${e.sellerProfileName || e.sellerProfileId} · ${e.researchedAt || ""}`}
            >
              <span
                className="drHistoryDot"
                style={{ background: qualityColor(e.dataQuality) }}
              />
              <span className="drHistorySellerName">
                {e.sellerProfileName || "Untitled seller"}
              </span>
              <span className="drHistoryWhen">{formatWhen(e.researchedAt)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default ResearchHistoryStrip;
